import { Can } from '@/Can';
import { TemplateWithPOIs } from '@/types';
import {
    Checkbox,
    FormControl,
    FormControlLabel,
    FormLabel,
    Skeleton,
    Stack,
    Tooltip,
} from '@mui/material';
import { TooltipProps } from './styles';
import { useAtom } from 'jotai';
import * as state from '@/components/Analysis/state';
import { useCallback, useMemo } from 'react';
import { primary } from '@/theme/colors';
import { theme } from '@/theme/theme';
import { PointOfInterestTemplateTypeEnum } from '@/__generated__/graphql';
import { TemplateCheckBox } from './TemplateCheckBox';

const Loader = () => (
  <Stack spacing={2} sx={{ py: 1 }} data-testid="pois-loader">
    {Array.from({ length: 6 }, (_, index) => (
      <Skeleton key={index} variant="rectangular" height={26} />
    ))}
  </Stack>
);

type Props = {
  loading: boolean;
  allTemplatesWithPOIs: TemplateWithPOIs[];
};

export const DeckplanPoiToggle = ({ loading, allTemplatesWithPOIs }: Props) => {
  const templatesWithoutWorkscopes = allTemplatesWithPOIs.filter(
    (item) => item.type !== PointOfInterestTemplateTypeEnum.PaintBlockVolumeOfInterest
  );

  const [selectedTemplates, setSelectedTemplates] = useAtom(state.pointOfInterestTemplates);

  const { isAllTemplatesSelected, isSomeTemplatesSelected } = useMemo(() => {
    const totalTemplates = templatesWithoutWorkscopes.length;
    const selectedCount = selectedTemplates.length;
    return {
      isAllTemplatesSelected: selectedCount === totalTemplates && selectedCount > 0,
      isSomeTemplatesSelected: selectedCount > 0 && selectedCount < totalTemplates,
    };
  }, [selectedTemplates, templatesWithoutWorkscopes]);

  const handleSelectAllTemplates = useCallback(() => {
    setSelectedTemplates(
      selectedTemplates.length === templatesWithoutWorkscopes.length
        ? []
        : templatesWithoutWorkscopes
    );
  }, [setSelectedTemplates, selectedTemplates.length, templatesWithoutWorkscopes]);

  const selectAllTemplatesCheckbox = useMemo(
    () => (
      <Checkbox
        checked={isAllTemplatesSelected}
        data-testid="all-template-checkbox"
        indeterminate={isSomeTemplatesSelected}
        sx={{
          color: primary.main,
          '&.Mui-checked': {
            color: theme.palette.common.darkBlue,
          },
        }}
      />
    ),
    [isAllTemplatesSelected, isSomeTemplatesSelected]
  );

  const handleSelectTemplate = useCallback(
    (template: TemplateWithPOIs) => {
      setSelectedTemplates((previousSelectedTemplates) =>
        previousSelectedTemplates.some(({ id }) => id === template.id)
          ? previousSelectedTemplates.filter(({ id }) => id !== template.id)
          : [...previousSelectedTemplates, template]
      );
    },
    [setSelectedTemplates]
  );

  return (
    <Can I="read" a="PointOfInterest">
      <FormControl>
        {loading && <Loader />}
        {!loading && (
          <>
            <FormLabel sx={{ mt: 1, mb: 1 }}>Point of Interest</FormLabel>
            <Tooltip
              title={
                templatesWithoutWorkscopes.some((template) => template.pointOfInterests.length)
                  ? ''
                  : 'No Points of Interest exist'
              }
              arrow
              placement="right"
              componentsProps={TooltipProps}
            >
              <FormControlLabel
                data-testid="all-poi-checkbox"
                value="all"
                onChange={handleSelectAllTemplates}
                control={selectAllTemplatesCheckbox}
                label="All"
                sx={{ width: 'fit-content' }}
                disabled={
                  !templatesWithoutWorkscopes.some(
                    (template) => template.pointOfInterests.length > 0
                  )
                }
              />
            </Tooltip>
            {templatesWithoutWorkscopes.map((template) => {
              return (
                <TemplateCheckBox
                  key={template.id}
                  templateId={template.id}
                  templateName={template.name}
                  onChange={() => handleSelectTemplate(template)}
                  disabled={template.pointOfInterests.length === 0}
                />
              );
            })}
          </>
        )}
      </FormControl>
    </Can>
  );
};
